import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

import { trackHeapEvent, noticeError } from '@ratehub/base-ui';
import {
    CreditCardDocument,
    CREDIT_CARD_DOC_TYPES,
} from '@ratehub/documents/cc-journey-document/stable';
import { getCreditCardResumeURL } from '@ratehub/cc-common';


dayjs.extend(isSameOrAfter);

/**
 * Extract credit card rows from profile documents
 * Rows will only contain the latest document of each
 * unique credit card document type
 * i.e. each latest unique hosted app and unique Cardfinder
 * @param documents
 * @param locale
 * @returns filtered and sorted credit card rows
 */
function extractCreditCardRows(documents, locale) {
    const extractedCardFinderRows = documents
        .filter(filterValidCardFinderDocument)
        .sort(sortByLastModified)
        .slice(0, 1) // Cardfinder only required to show the most recent card search
        .map(extractCreditCardRow(locale, CREDIT_CARD_DOC_TYPES.CARD_FINDER));

    const extractedHostedAppRows = getUniqueHostedAppDocuments(
        documents.filter(filterValidHostedAppDocument),
    ).map(extractCreditCardRow(locale, CREDIT_CARD_DOC_TYPES.HOSTED_APP));

    // Concatting hosted app rows first because they always take priority
    // being displayed over Cardfinder rows
    const creditCardRows = extractedHostedAppRows.concat(
        extractedCardFinderRows,
    );

    if (creditCardRows[0]) {
        creditCardRows[0].isMostRecent = true;
    }

    return creditCardRows;
}

/**
 * To parse hosted app documents and return sorted array
 * that contains each most recent unique hosted app
 * @param {object[]} documents
 * @returns {object[]} sorted array of most recent and unique hosted app documents
 */
function getUniqueHostedAppDocuments(documents) {
    let hostedAppDocumentsArray = [];
    documents.forEach((document) => {
        const currentCard = document.journey.application.card;
        const existingIndex = hostedAppDocumentsArray.findIndex(
            (item) => item[0]?.journey?.application?.card === currentCard,
        );
        if (existingIndex >= 0) {
            hostedAppDocumentsArray[existingIndex].push(document);
        } else {
            hostedAppDocumentsArray.push([ document ]);
        }
    });

    return hostedAppDocumentsArray.map((cardDocumentArray) =>
        // Hosted app only required to show the most recent for each unique application
        cardDocumentArray.sort(sortByLastModified)[0],
    ).sort(sortByLastModified);
}

function ignoreOldDocuments(document) {
    const today = dayjs().startOf('day');
    const lastUpdated = dayjs(document.updatedAt).startOf('day');

    return today.diff(lastUpdated, 'days') <= 30;
}

function sortByLastModified(a, b) {
    const aLastModified = dayjs(a.updatedAt);
    const bLastModified = dayjs(b.updatedAt);

    return bLastModified.diff(aLastModified);
}

function filterValidCardFinderDocument(document) {
    return !!document?.journey?.cardPreference?.scenario
        && ignoreOldDocuments(document)
        && isValidCreditCardDocument(document);
}

function filterValidHostedAppDocument(document) {
    return !!document.journey?.application?.card
        && !document.journey.application.status
        && ignoreOldDocuments(document)
        && isValidCreditCardDocument(document);
}

function isValidCreditCardDocument(document) {
    const isValid = CreditCardDocument.is(document);

    if (!isValid) {
        noticeError(
            new Error('[isValidCreditCardDocument] Error document snapshot is not a valid CreditCardDocument'),
            { documentId: document.id },
        );

        trackHeapEvent('Failed to load dashboard document', {
            documentID: document.id,
        });
    }

    return isValid;
}

function extractCreditCardRow(locale, creditCardDocumentType) {
    return document => {
        const application = document.journey?.application;

        return {
            key: document.journey?.id,
            creditCardDocumentType: creditCardDocumentType,
            document: {
                id: document.id,
                modified: document.updatedAt,
                resumeHREF: getCreditCardResumeURL(
                    document.id,
                    locale,
                    creditCardDocumentType,
                    application?.card,
                ),
                cardName: application?.cardName,
            },
        };
    };
}


export default extractCreditCardRows;
